import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface Certificate {
    id: number;
    registration_id: number;
    type: string;
    file_path: string | null;
    local_file_path?: string | null;
    local_file_url?: string | null;
    signed_file_url?: string | null;
    is_completed: boolean;
    is_emailed: boolean;
    created_at: string;
    updated_at: string;
    batch?: any;
    registration?: {
        id: number;
        participant_id: number;
        participant?: {
            participant_id: number;
            full_name: string;
            email: string;
            institution: string;
        };
        conference?: {
            conference_id: number;
            name: string;
        };
    };
}

export interface TteBatch {
    id: number;
    period_id: number;
    status: string;
    total_documents: number;
    completed_documents: number;
    failed_documents: number;
    created_at: string;
    updated_at: string;
}

export const exportAdminCertificates = async (
    type?: string,
    isCompleted?: boolean,
    isEmailed?: boolean,
    periodId?: string,
    search?: string,
    conferenceId?: string,
    roomId?: string,
    includeSignedUrl: boolean = true,
    includeLocalUrl: boolean = false
) => {
    const response = await axiosClient.get("/private/certificates/export", {
        params: {
            type,
            is_completed: isCompleted,
            is_emailed: isEmailed,
            period_id: periodId,
            search,
            conference_id: conferenceId,
            room_id: roomId,
            include_signed_url: includeSignedUrl,
            include_local_url: includeLocalUrl
        },
        responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'certificates_export.xlsx';
    if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2)
            fileName = fileNameMatch[1];
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

export const useAdminCertificates = (
    page = 1, 
    per_page = 50,
    type?: string,
    is_completed?: boolean,
    is_emailed?: boolean,
    period_id?: string,
    search?: string,
    conference_id?: string,
    room_id?: string,
    is_file_generated?: boolean
) => {
    return useQuery({
        queryKey: ["admin-certificates", page, per_page, type, is_completed, is_emailed, period_id, search, conference_id, room_id, is_file_generated],
        queryFn: async () => {
            const params: any = { page, per_page };
            if (type) params.type = type;
            if (is_completed !== undefined) params.is_completed = is_completed;
            if (is_emailed !== undefined) params.is_emailed = is_emailed;
            if (period_id) params.period_id = period_id;
            if (search) params.search = search;
            if (conference_id) params.conference_id = conference_id;
            if (room_id) params.room_id = room_id;
            if (is_file_generated !== undefined) params.is_file_generated = is_file_generated;

            const { data } = await axiosClient.get("/private/certificates", { 
                params,
                withCredentials: true 
            });
            return data;
        },
        refetchOnWindowFocus: false,
    });
};

export interface RegistrationSearchResult {
    id: number;
    name: string;
    email: string;
    extra: string | null;
}

// Dipakai dialog Generate Lokal mode "Peserta Spesifik" - mencari CALON peserta/pemakalah/speaker
// dari tabel registrasi (bukan tabel certificates, karena sertifikatnya mungkin belum ada).
export const useRegistrationSearch = (type: string, search: string, conferenceId?: string) => {
    return useQuery({
        queryKey: ["admin-registration-search", type, search, conferenceId],
        queryFn: async () => {
            const params: any = { type, search: search || undefined };
            if (conferenceId && conferenceId !== "all") params.conference_id = conferenceId;
            const { data } = await axiosClient.get("/private/certificates/registration-search", { params });
            return data.data as RegistrationSearchResult[];
        },
    });
};

export const useAdminTteBatches = (page = 1, perPage = 20, search = "") => {
    return useQuery({
        queryKey: ["admin-tte-batches", page, perPage, search],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/certificates/batches", { 
                params: { page, per_page: perPage, search },
                withCredentials: true 
            });
            return data;
        },
    });
};

export const useBatchCertificates = (batchId: number | null, page = 1, perPage = 10, search = "") => {
    return useQuery({
        queryKey: ["admin-batch-certificates", batchId, page, perPage, search],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/private/certificates/batches/${batchId}/certificates`, {
                params: { page, per_page: perPage, search },
                withCredentials: true
            });
            return data;
        },
        enabled: !!batchId
    });
};

export const useGenerateLocalCertificates = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ type, limit, ids, force, conference_id, certificate_type_id, room_id }: { type: string; limit?: number; ids?: string; force?: boolean; conference_id?: string; certificate_type_id?: string; room_id?: string }) => {
            try {
                const { data } = await axiosClient.post("/private/certificates/generate-local", 
                    { type, limit, ids, force, conference_id, certificate_type_id, room_id }, 
                    { withCredentials: true }
                );
                return data;
            } catch (error: any) {
                const errorMsg = error.response?.data?.error || "Terjadi kesalahan pada server saat generate lokal.";
                const details = error.response?.data?.details || "";
                
                console.error("Gagal Generate Lokal:", details);
                
                throw new Error(errorMsg);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export const useDeleteCertificate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            try {
                const { data } = await axiosClient.delete(`/private/certificates/${id}`, { withCredentials: true });
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Gagal menghapus sertifikat.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

// Melepas status TTE hanya untuk satu sertifikat (mis. ada 1 file salah di antara banyak file
// yang sudah selesai TTE), supaya bisa digenerate ulang + diajukan TTE ulang tanpa membatalkan
// batch yang berisi file-file lain yang sudah benar.
export const useResetTteCertificate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            try {
                const { data } = await axiosClient.post(`/private/certificates/${id}/reset-tte`, {}, { withCredentials: true });
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Gagal mereset status TTE sertifikat.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export interface BulkDeleteFilterParams {
    type?: string;
    period_id?: string;
    conference_id?: string;
    room_id?: string;
    is_completed?: boolean;
    is_emailed?: boolean;
    search?: string;
}

export interface BulkDeletePreviewResult {
    status: string;
    total_matched: number;
    locked_count: number;
    deletable_count: number;
}

export interface BulkDeleteResult {
    status: string;
    message: string;
    deleted_count: number;
    skipped_count: number;
}

// ids diberikan -> hapus berdasarkan ID spesifik (checklist). ids kosong -> hapus berdasarkan filter.
export const previewBulkDeleteCertificates = async (ids?: number[], filters?: BulkDeleteFilterParams) => {
    const params: any = ids && ids.length > 0 ? { ids } : { ...filters };
    const { data } = await axiosClient.get<BulkDeletePreviewResult>("/private/certificates/bulk-delete-preview", {
        params,
    });
    return data;
};

export const useBulkDeleteCertificates = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ ids, filters }: { ids?: number[]; filters?: BulkDeleteFilterParams }) => {
            try {
                const params: any = ids && ids.length > 0 ? { ids } : { ...filters };
                const { data } = await axiosClient.delete<BulkDeleteResult>("/private/certificates/bulk", {
                    params,
                    withCredentials: true,
                });
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Gagal menghapus sertifikat secara massal.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export const useCancelTteBatch = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            try {
                const { data } = await axiosClient.delete(`/private/certificates/batches/${id}/cancel`, { withCredentials: true });
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Gagal membatalkan batch TTE.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-tte-batches"] });
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export const useDeleteTteBatch = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            try {
                const { data } = await axiosClient.delete(`/private/certificates/batches/${id}`, { withCredentials: true });
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Gagal menghapus batch TTE.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-tte-batches"] });
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export const useSendTteCertificates = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: any) => {
            try {
                const { data } = await axiosClient.post("/private/certificates/send-tte", payload, { withCredentials: true });
                return data;
            } catch (error: any) {
                const errorData = error.response?.data;
                const message = errorData?.error || errorData?.message || "Terjadi kesalahan saat mengirim antrean TTE.";
                throw new Error(message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
            queryClient.invalidateQueries({ queryKey: ["admin-tte-batches"] });
        },
    });
};

export const useCheckTteStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (tte_id: string) => {
            try {
                // Hitting the webhook URL using GET (as handled by Route::any) or POST
                const { data } = await axiosClient.post(`/callback/tte/${tte_id}`, {}, { withCredentials: true });
                return data;
            } catch (error: any) {
                const errorData = error.response?.data;
                const message = errorData?.error || errorData?.message || "Gagal mengecek status TTE.";
                throw new Error(message);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-tte-batches"] });
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
        },
    });
};

export const useSendEmailCertificates = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: any) => {
            try {
                const { data } = await axiosClient.post("/private/certificates/send-email",
                    payload,
                    { withCredentials: true }
                );
                return data;
            } catch (error: any) {
                throw new Error(error.response?.data?.error || "Terjadi kesalahan saat mengirim email.");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-certificates"] });
            queryClient.invalidateQueries({ queryKey: ["admin-batch-certificates"] });
        },
    });
};
